import React, { useState, useEffect, useCallback } from "react";
import axios from "../../utils/SOP-api";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function LeaveApplications() {
  const [employeeId, setEmployeeId] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentLeaveId, setCurrentLeaveId] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState("");
  const navigate = useNavigate();

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (employeeId) params.employeeId = employeeId;
      if (createdAt) params.createdAt = createdAt;

      const response = await axios.get("/leave/data-get", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setLeaves(response.data.leaves || []);
    } catch (error) {
      console.error("Error fetching leaves:", error);
      if (error.response) {
        alert(error.response.data.error || "Something went wrong!");
      } else {
        alert("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [employeeId, createdAt]);

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  const handleApproval = async (leaveId, status) => {
    try {
      const data = { status };
      if (status === "rejected" && rejectionMessage) {
        data.rejectionMessage = rejectionMessage;
      }

      await axios.patch(`/leave/${leaveId}/status`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchLeaves();
      setIsModalOpen(false);
      setRejectionMessage("");
    } catch (error) {
      console.error("Error updating leave status:", error);
      alert("Error updating leave status. Please try again.");
    }
  };

  const openRejectionModal = (leaveId) => {
    setCurrentLeaveId(leaveId);
    setIsModalOpen(true);
  };

  const closeRejectionModal = () => {
    setIsModalOpen(false);
    setRejectionMessage("");
  };

  const pageStyle = {
    position: "relative",
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "24px 32px 48px",
    boxSizing: "border-box",
  };

  return (
    <div style={pageStyle}>
      <IconButton
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          color: "#4a4a4a",
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <div style={{ textAlign: "center", marginTop: 20, marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            marginBottom: 8,
            fontSize: "1.85rem",
            fontWeight: 700,
            letterSpacing: "-0.5px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Leave Applications
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: "0.95rem",
            color: "#64748b",
            fontWeight: 400,
          }}
        >
          View, filter, and manage leave requests
        </p>
      </div>

      <div style={styles.filterSection}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Employee ID</label>
              <input
                type="text"
                placeholder="Enter employee ID"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Date</label>
              <input
                type="date"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.countersContainer}>
            <div style={styles.counterItem}>
              <span style={styles.counterLabel}>Total:</span>
              <span style={styles.counterValue}>{leaves.length}</span>
            </div>
            <div style={styles.counterItem}>
              <span style={styles.counterLabel}>Approved:</span>
              <span style={styles.counterValue}>
                {leaves.filter((l) => l.status === "approved").length}
              </span>
            </div>
            <div style={styles.counterItem}>
              <span style={styles.counterLabel}>Rejected:</span>
              <span style={styles.counterValue}>
                {leaves.filter((l) => l.status === "rejected").length}
              </span>
            </div>
          </div>

          <button
            style={styles.clearAllButton}
            onClick={() => {
              setEmployeeId("");
              setCreatedAt("");
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
            gap: 16,
          }}
        >
          <span style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
            Loading...
          </span>
        </div>
      ) : leaves.length === 0 ? (
        <div style={styles.noData}>
          <img
            src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png"
            alt="No data"
            style={styles.noDataImage}
          />
          <p style={{ margin: 0, fontSize: "1rem", color: "#64748b" }}>No leave applications found</p>
        </div>
      ) : (
        <div style={styles.cardGrid}>
          {leaves.map((leave) => (
            <div key={leave._id} style={styles.card}>
              <div style={styles.cardHeader}>
                <span style={styles.employeeId}>{leave.employeeId}</span>
                <span style={styles.statusBadge(leave.status)}>
                  {leave.status}
                </span>
              </div>

              <div style={styles.cardBody}>
                {leave.email && (
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Email:</span>
                    <span style={styles.value}>{leave.email}</span>
                  </div>
                )}

                <div style={styles.cardRow}>
                  <span style={styles.label}>Leave Type:</span>
                  <span style={styles.value}>{leave.leaveType}</span>
                </div>

                {leave.leaveDuration && (
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Duration:</span>
                    <span style={styles.value}>{leave.leaveDuration}</span>
                  </div>
                )}

                {leave.fromDate && leave.toDate ? (
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Dates:</span>
                    <span style={styles.value}>
                      {new Date(leave.fromDate).toLocaleDateString("en-GB")} -{" "}
                      {new Date(leave.toDate).toLocaleDateString("en-GB")}
                    </span>
                  </div>
                ) : (
                  leave.date && (
                    <div style={styles.cardRow}>
                      <span style={styles.label}>Date:</span>
                      <span style={styles.value}>
                        {new Date(leave.date).toLocaleDateString("en-GB")}
                        {leave.session &&
                          ` (${
                            leave.session === "AM" ? "Morning" : "Afternoon"
                          })`}
                      </span>
                    </div>
                  )
                )}

                {leave.numberOfDays && (
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Days:</span>
                    <span style={styles.value}>
                      {leave.numberOfDays} day(s)
                    </span>
                  </div>
                )}

                {leave.reason && (
                  <div style={styles.reasonContainer}>
                    <div style={styles.reasonLabel}>Reason:</div>
                    <div style={styles.reasonText}>
                      {leave.reason.split("\n").map((paragraph, i) => (
                        <p key={i} style={styles.reasonParagraph}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {leave.rejectionMessage && (
                  <div style={styles.rejectionContainer}>
                    <div style={styles.rejectionLabel}>Rejection Reason:</div>
                    <div style={styles.rejectionText}>
                      {leave.rejectionMessage
                        .split("\n")
                        .map((paragraph, i) => (
                          <p key={i} style={styles.rejectionParagraph}>
                            {paragraph}
                          </p>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.timestampContainer}>
                {leave.createdAt && (
                  <div style={styles.cardRow}>
                    <span style={styles.label}>Created At:</span>
                    <span style={styles.value}>
                      {new Date(leave.createdAt).toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                )}
              </div>
              {leave.status === "pending" && (
                <div style={styles.cardFooter}>
                  <button
                    style={styles.approveButton}
                    onClick={() => handleApproval(leave._id, "approved")}
                  >
                    Approve
                  </button>
                  <button
                    style={styles.rejectButton}
                    onClick={() => openRejectionModal(leave._id)}
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Rejection Modal */}
      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Reject Leave Application</h3>
              <button
                style={styles.modalCloseButton}
                onClick={closeRejectionModal}
              >
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <p style={styles.modalText}>
                Please provide a reason for rejection:
              </p>
              <textarea
                value={rejectionMessage}
                onChange={(e) => setRejectionMessage(e.target.value)}
                style={styles.textarea}
                placeholder="Enter rejection reason..."
                rows="4"
              />
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.modalCancelButton}
                onClick={closeRejectionModal}
              >
                Cancel
              </button>
              <button
                style={styles.modalSubmitButton}
                onClick={() => handleApproval(currentLeaveId, "rejected")}
                disabled={!rejectionMessage.trim()}
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveApplications;

const styles = {
  filterSection: {
    maxWidth: 1200,
    margin: "0 auto 28px",
    padding: "24px 28px",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(12px)",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04)",
  },
  filterRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: "1.5rem",
    flexWrap: "wrap",
  },
  filterGroup: {
    display: "flex",
    gap: "1.5rem",
    flexGrow: 1,
  },
  countersContainer: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
    marginRight: "auto",
  },
  counterItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    padding: "0.5rem 1rem",
    borderRadius: 10,
    minWidth: "80px",
    border: "1px solid #e2e8f0",
  },
  counterLabel: {
    fontSize: "0.85rem",
    color: "#64748b",
    fontWeight: "500",
  },
  counterValue: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "#334155",
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  },
  inputLabel: {
    fontSize: "0.875rem",
    color: "#334155",
    fontWeight: "600",
  },
  input: {
    padding: "10px 14px",
    fontSize: "0.95rem",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    minWidth: 200,
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    outline: "none",
  },
  clearAllButton: {
    padding: "10px 18px",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
    alignSelf: "flex-end",
    marginLeft: "auto",
  },
  noData: {
    textAlign: "center",
    marginTop: "3rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "1rem",
  },
  noDataImage: {
    width: 120,
    opacity: 0.6,
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "1.5rem",
    maxWidth: 1200,
    margin: "0 auto",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(12px)",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.04)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem 1.25rem",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    borderBottom: "1px solid #e2e8f0",
  },
  employeeId: {
    fontSize: "1.05rem",
    fontWeight: "600",
    color: "#334155",
  },
  reasonContainer: {
    marginTop: "1rem",
    padding: "1rem",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    borderRadius: 12,
    borderLeft: "4px solid #667eea",
  },
  reasonLabel: {
    fontWeight: "600",
    color: "#334155",
    marginBottom: "0.5rem",
    fontSize: "0.95rem",
  },
  reasonText: {
    color: "#475569",
    lineHeight: 1.5,
    whiteSpace: "pre-line",
  },
  reasonParagraph: {
    margin: "0.5rem 0",
  },
  rejectionContainer: {
    marginTop: "1rem",
    padding: "1rem",
    backgroundColor: "#fef2f2",
    borderRadius: 12,
    borderLeft: "4px solid #dc2626",
  },
  rejectionLabel: {
    fontWeight: "600",
    color: "#dc2626",
    marginBottom: "0.5rem",
    fontSize: "0.95rem",
  },
  rejectionText: {
    color: "#991b1b",
    lineHeight: 1.5,
    whiteSpace: "pre-line",
  },
  rejectionParagraph: {
    margin: "0.5rem 0",
  },
  statusBadge: (status) => ({
    padding: "0.3rem 0.75rem",
    borderRadius: 20,
    fontSize: "0.8rem",
    fontWeight: "600",
    backgroundColor:
      status === "approved"
        ? "#dcfce7"
        : status === "rejected"
        ? "#fee2e2"
        : "#e0e7ff",
    color:
      status === "approved"
        ? "#16a34a"
        : status === "rejected"
        ? "#dc2626"
        : "#4f46e5",
  }),
  cardBody: {
    padding: "1.25rem 1.25rem",
    flexGrow: "1",
  },
  cardRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "0.75rem",
  },
  label: {
    fontWeight: "500",
    color: "#64748b",
    fontSize: "0.9rem",
  },
  value: {
    color: "#334155",
    textAlign: "right",
    maxWidth: "60%",
    wordBreak: "break-word",
    fontSize: "0.9rem",
  },
  rejectionMessage: {
    color: "#dc2626",
    fontStyle: "italic",
    textAlign: "right",
    maxWidth: "60%",
    wordBreak: "break-word",
  },
  timestampContainer: {
    padding: "0.75rem 1.25rem",
    borderTop: "1px solid #e2e8f0",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    fontSize: "0.85rem",
    color: "#64748b",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    gap: "0.75rem",
    padding: "1rem 1.25rem",
    background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    borderTop: "1px solid #e2e8f0",
  },
  approveButton: {
    padding: "0.6rem 1.2rem",
    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
    color: "white",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
    boxShadow: "0 2px 8px rgba(22, 163, 74, 0.3)",
  },
  rejectButton: {
    padding: "0.6rem 1.2rem",
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
    boxShadow: "0 2px 8px rgba(220, 38, 38, 0.25)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
    width: 500,
    maxWidth: "90%",
    maxHeight: "90vh",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e2e8f0",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1.25rem 1.5rem",
    borderBottom: "1px solid #e2e8f0",
  },
  modalTitle: {
    fontSize: "1.2rem",
    fontWeight: "600",
    color: "#334155",
    margin: 0,
  },
  modalCloseButton: {
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#64748b",
    padding: "0 0.5rem",
  },
  modalBody: {
    padding: "1.25rem 1.5rem",
    flexGrow: "1",
  },
  modalText: {
    marginBottom: "1rem",
    color: "#475569",
    fontSize: "0.95rem",
  },
  textarea: {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    fontSize: "0.95rem",
    resize: "vertical",
    minHeight: 120,
    boxSizing: "border-box",
    outline: "none",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.75rem",
    padding: "1rem 1.5rem",
    borderTop: "1px solid #e2e8f0",
  },
  modalCancelButton: {
    padding: "0.6rem 1.2rem",
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
  },
  modalSubmitButton: {
    padding: "0.6rem 1.2rem",
    backgroundColor: "#dc2626",
    color: "white",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: "600",
  },
};
